"use client";

import { WcIconButton, WcStack, WcTypography } from "@bpipartners/webui";
import { FetchMenuRow } from "@guberno/data-access";
import React, { useRef } from "react";
import { Tree, MultiBackend, getBackendOptions, NodeModel, DndProvider } from "@minoru/react-dnd-treeview";
import { CustomDrawerNode } from "./CustomDrawerNode.View";
import styles from "./Tree.module.css";
import { usePathname, useRouter } from "next/navigation";
import { useTheme } from "@guberno/utils/theme";
import { AppContext, TaskContext } from "@guberno/utils/context";
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarRightCollapse } from "react-icons/tb";
import _ from "lodash";
import { useWindowDimensions } from "@guberno/utils/hooks";

interface FlatDrawerProps {
  treeData: FetchMenuRow[];
}

const FlatDrawer = ({ treeData }: FlatDrawerProps) => {
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;
  const windowSize = useWindowDimensions();
  const pathName = usePathname();
  const router = useRouter();
  const theme = useTheme();
  const [localTreeData, setLocalTreeData] = React.useState<NodeModel[]>();
  const [selectedNode, setSelectedNode] = React.useState<NodeModel>();
  const [initialOpen, setInitialOpen] = React.useState<number[]>([]);
  const [openUserProfile, SetOpenUserProfile] = React.useState(false);
  const [currentID, setCurrentID] = React.useState<number | string>(0);
  const { localAppState, localIsDrawerExpandedState, handleSetLocalIsDrawerExpandedState } = React.useContext(AppContext);

  const isDrawerExpanded = localIsDrawerExpandedState;

  const [isDrawerOnHover, setIsDrawerOnHover] = React.useState(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const { onOpenTaskSideSheet, openTaskSideSheet } = React.useContext(TaskContext);
  const treeRef = useRef(null);

  const handleUserProfileClicked = () => {
    SetOpenUserProfile(!openUserProfile);
  };

  useIsomorphicLayoutEffect(() => {
    if (treeData) {
      const treeList: NodeModel[] = [];
      treeData.forEach((element) => {
        const treeItem: NodeModel = {
          //@ts-ignore
          id: element.menuID,
          parent: element.parentID || 0,
          //@ts-ignore
          text: element.name,
          //@ts-ignore
          order: element.menuOrder,
          droppable: false,
          data: {
            type: element.type,
            route: element.route,
            icon: element.icon,
          },
        };
        treeList.push(treeItem);
      });

      setLocalTreeData([...treeList]);
      if (pathName) {
        const splitPath = pathName.split("/");
        const lastNumber = splitPath[splitPath.length - 1];
        const mainPath = "/" + splitPath[1] + "/" + splitPath[2];
        // Deep routes with a dynamic segment (e.g. /circleManagement/<uuid> —
        // only two segments) have mainPath === pathName, so also try the first
        // path segment as a fallback, or the highlight stays stale.
        const rootPath = "/" + splitPath[1];
        if (lastNumber === "") {
          setSelectedNode(treeList?.find((item) => item.text === "home"));
        } else {
          const findFullItem = treeList?.find(
            //@ts-ignore
            (item) => item.data.route === pathName
          );

          if (findFullItem) {
            setFoundItem(findFullItem);
          } else {
            const findFirstLevelItem =
              treeList?.find(
                //@ts-ignore
                (item) => item.data.route === mainPath
              ) ??
              treeList?.find(
                //@ts-ignore
                (item) => item.data.route === rootPath
              );
            if (findFirstLevelItem) {
              setFoundItem(findFirstLevelItem);
            }
          }
        }
      }
    }
  }, [treeData, pathName]);

  const setFoundItem = (findItem) => {
    setSelectedNode(findItem);
    if (localIsDrawerExpandedState) {
      setInitialOpen([parseInt(findItem.parent.toString(), 10), parseInt(findItem.id.toString(), 10)]);
    }
  };

  const handleLogoutClick = () => {
    window.location.href = "/api/logout";
  };

  const handleUserOrLogoutClick = (node: NodeModel) => {
    switch (node.text) {
      case "userProfile":
        handleUserProfileClicked();
        break;

      default:
        handleLogoutClick();
        break;
    }
  };

  const handleAppRouteClick = (node: NodeModel) => {
    const splitCurrentRoute = pathName.split("/");
    //@ts-ignore
    const splitNewRoute = node.data.route.split("/");

    const isCurrentSupport = splitCurrentRoute[1].includes("support");
    const isNewSupport = splitNewRoute[1].includes("support");
    if (openTaskSideSheet) {
      onOpenTaskSideSheet();
    }
    if (!isCurrentSupport && !isNewSupport) {
      //@ts-ignore
      router.push(node.data.route);
      setSelectedNode(node);
    } else if (isCurrentSupport && isNewSupport) {
      //@ts-ignore
      const NewSupportRoute = node.data.route.replace("/support/", "/");
      router.push(NewSupportRoute);
      setSelectedNode(node);
    } else {
      //@ts-ignore
      window.location.href = node.data.route;
      setSelectedNode(node);
    }
  };

  const handleOnSelect = (node: NodeModel) => {
    if (pathName && node) {
      //@ts-ignore
      switch (node.data.type) {
        case "link":
          handleAppRouteClick(node);
          break;
        default:
          handleUserOrLogoutClick(node);
          break;
      }
    }
  };

  const handleDrawerToggle = () => {
    if (isDrawerExpanded) {
      treeRef.current?.closeAll();
    } else if (selectedNode && selectedNode.parent) {
      treeRef.current?.open(selectedNode.parent);
    }
    handleSetLocalIsDrawerExpandedState(!isDrawerExpanded);
    setIsDrawerOnHover(false);
  };

  const handleMouseEnter = () => {
    if (!isDrawerExpanded) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setIsDrawerOnHover(true);
        treeRef.current?.open(selectedNode.parent);
      }, 300);
    }
  };

  const handleMouseLeave = () => {
    if (!isDrawerExpanded) {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      setIsDrawerOnHover(false);
      treeRef.current?.closeAll();
    }
  };

  React.useEffect(
    () => () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    },
    []
  );

  return (
    <WcStack
      position="relative"
      width={isDrawerExpanded ? theme.dimensions.appDrawerWidth : theme.dimensions.appDrawerCollapsedWidth}
      minWidth={isDrawerExpanded ? theme.dimensions.appDrawerWidth : theme.dimensions.appDrawerCollapsedWidth}
      height={"100%"}
      id="FlatDrawer"
      sx={{
        backgroundColor: theme.appColors.surfaceBright,
        transition: "all 0.2s ease-in-out",
        overflow: "visible",
        zIndex: 1000,
      }}
    >
      <WcStack
        id="FlatDrawerMainStack"
        position={isDrawerExpanded ? "relative" : "absolute"}
        left={0}
        top={0}
        width={isDrawerExpanded || isDrawerOnHover ? theme.dimensions.appDrawerWidth : theme.dimensions.appDrawerCollapsedWidth}
        minWidth={
          isDrawerExpanded || isDrawerOnHover ? theme.dimensions.appDrawerWidth : theme.dimensions.appDrawerCollapsedWidth
        }
        height={"100%"}
        paddingLeft={isDrawerExpanded || isDrawerOnHover ? 8 : 4}
        paddingRight={isDrawerExpanded || isDrawerOnHover ? 8 : 4}
        paddingTop={4}
        paddingBottom={4}
        sx={{
          backgroundColor: theme.appColors.surfaceBright,
          transition: "all 0.2s ease-in-out",
          zIndex: isDrawerOnHover ? 1001 : "auto",
          boxShadow: isDrawerOnHover ? "4px 0 10px rgba(0, 0, 0, 0.1)" : "none",
          overflow: "hidden",
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <WcStack
          height={windowSize.height - 120}
          sx={{
            overflowY: "scroll",
            overflowX: "hidden",
            scrollbarWidth: "none",
            "&::MsOverflowStyle": "none",
            "&::-webkit-scrollbar": {
              width: 0,
              display: "none",
            },
          }}
        >
          <WcStack direction={"row"} justifyContent={"flex-end"}>
            {(isDrawerExpanded || isDrawerOnHover) && (
              <WcIconButton sx={{ fontSize: 18 }} onClick={handleDrawerToggle}>
                {isDrawerExpanded ? <TbLayoutSidebarLeftCollapse /> : <TbLayoutSidebarRightCollapse />}
              </WcIconButton>
            )}
          </WcStack>
          {localTreeData && localTreeData.length > 0 && (
            <DndProvider context={window} backend={MultiBackend} options={getBackendOptions()}>
              <Tree
                tree={localTreeData}
                rootId={0}
                ref={treeRef}
                initialOpen={initialOpen}
                enableAnimateExpand={true}
                insertDroppableFirst={false}
                render={(node, { onToggle, isOpen, ...rest }) => (
                  <CustomDrawerNode
                    //@ts-ignore
                    node={node}
                    isSelected={node.id === selectedNode?.id}
                    isChildItem={node.parent !== 0}
                    isParentOfSelectedChild={selectedNode?.parent === node.id}
                    isDrawerExpaned={isDrawerExpanded || isDrawerOnHover}
                    {...rest}
                    onToggle={(id) => {
                      if (currentID === node.id) {
                        setCurrentID(0);
                        treeRef.current?.closeAll();
                      } else {
                        treeRef.current?.closeAll();
                        setCurrentID(id);
                        setTimeout(() => {
                          treeRef.current?.open(id);
                        }, 5);
                      }
                    }}
                    onSelect={handleOnSelect}
                    isOpen={isOpen}
                  />
                )}
                dragPreviewRender={() => null}
                canDrag={() => false}
                canDrop={() => false}
                sort={false}
                onDrop={() => {}}
                classes={{
                  root: styles.treeRoot,
                  draggingSource: styles.draggingSource,
                  dropTarget: styles.dropTarget,
                  listItem: styles.listStyle,
                  placeholder: styles.placeholderContainer,
                  container: styles.container,
                }}
              />
            </DndProvider>
          )}
        </WcStack>

        {(isDrawerExpanded || isDrawerOnHover) && (
          <WcStack direction={"row"} justifyContent={"center"}>
            <WcTypography color={"GrayText"} variant="caption">
              {localAppState && localAppState.appVersion}
            </WcTypography>
          </WcStack>
        )}
      </WcStack>
    </WcStack>
  );
};
export default FlatDrawer;
